import { useTranslation } from 'react-i18next';
import type { Referral } from '@/hooks/useReferrals';
import { referDescriptionDisplay } from '@/lib/referral/xref01Labels';

// C2 (REF01-M): read-only view of the XREF01 fields kept in medcom_details.
// T1.7: a received XREF02 uses the same fields, plus FirstDateOfPeriod.
// Values come from an imported document, so they are rendered as plain text.

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function list(value: unknown): Rec[] {
  return Array.isArray(value) ? value.filter(isRec) : [];
}

const SECTION =
  'overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card  ';
const GRID = 'grid gap-px bg-ds-line  sm:grid-cols-2 lg:grid-cols-4';
const HEADING = 'text-xs font-medium uppercase tracking-wide text-ds-muted ';

function Cell({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="bg-ds-card px-4 py-3">
      <div className="mb-2">
        <span className={HEADING}>{label}</span>
      </div>
      <div className="whitespace-pre-wrap break-words text-sm font-medium text-ds-text">
        {value ?? '—'}
      </div>
    </div>
  );
}

function Block({ label, value, last }: { label: string; value: string | null; last?: boolean }) {
  return (
    <div className={`px-6 py-5 ${last ? '' : 'border-b border-ds-line '}`}>
      <div className="mb-2">
        <span className={HEADING}>{label}</span>
      </div>
      <div className="whitespace-pre-wrap text-sm text-ds-text">{value ?? '—'}</div>
    </div>
  );
}

function Heading({ children }: { children: string }) {
  return (
    <div className="border-b border-ds-line px-4 py-2.5">
      <span className={HEADING}>{children}</span>
    </div>
  );
}

function diagnosis(code: string | null, label: string | null): string | null {
  if (code && label) return `${code} — ${label}`;
  return code ?? label;
}

function address(rec: Rec): string | null {
  const postal = [text(rec.postCodeIdentifier), text(rec.districtName)].filter(Boolean).join(' ');
  const lines = [text(rec.streetName), text(rec.suburbName), postal].filter(
    (line): line is string => Boolean(line),
  );
  return lines.length > 0 ? lines.join('\n') : null;
}

export function MedcomXref01Details({ referral }: { referral: Referral }) {
  const { t } = useTranslation();
  const d = referral.medcom_details;
  const receiver = isRec(d.receiver) ? d.receiver : {};
  const referrer = isRec(d.referrer) ? d.referrer : {};
  const episodes = list(d.episodesOfCare);
  const lpr = episodes.find((e) => (text(e.type) ?? '').toUpperCase().startsWith('LPR'));
  const additional = list(d.additionalDiagnoses);
  const visits = list(d.hospitalVisitations);
  const receiverId = [text(receiver.identifier), text(receiver.identifierCode)]
    .filter(Boolean)
    .join(' · ');
  const referrerId = [text(referrer.identifier), text(referrer.identifierCode)]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-4">
      <section className={SECTION}>
        <Heading>
          {t(referral.medcom_type_code === 'XREF02' ? 'referrals.xref02_section_referral' : 'referrals.xref01_section_referral')}
        </Heading>
        <div className={GRID}>
          <Cell
            label={t('referrals.xref01_standard')}
            value={referral.medcom_type_code === 'XREF02' ? 'XREF02 (REF02)' : 'XREF01 (REF01)'}
          />
          <Cell label={t('referrals.xref01_referral_date')} value={text(d.referralDate)} />
          {referral.medcom_type_code === 'XREF02' && (
            <Cell label={t('referrals.xref02_first_date_of_period')} value={text(d.firstDateOfPeriod)} />
          )}
          <Cell label={t('referrals.xref01_letter_id')} value={referral.medcom_letter_identifier} />
          <Cell label={t('referrals.xref01_referral_id')} value={referral.episode_of_care_identifier} />
          <Cell label={t('referrals.xref01_lpr3_uuid')} value={lpr ? text(lpr.identifier) : null} />
          <Cell label={t('referrals.xref01_priority')} value={text(d.priority)} />
          <Cell label={t('referrals.xref01_referral_type')} value={text(d.referralStatus)} />
          <Cell label={t('referrals.xref01_transport')} value={text(d.transport)} />
          <Cell label={t('referrals.xref01_hospital_choice')} value={text(d.hospitalChoice)} />
        </div>
      </section>

      <section className={SECTION}>
        <Heading>{t('referrals.xref01_section_receiver')}</Heading>
        <div className={GRID}>
          <Cell label={t('referrals.xref01_ean')} value={text(receiver.ean)} />
          <Cell label={t('referrals.xref01_identifier')} value={receiverId || null} />
          <Cell label={t('referrals.xref01_organisation')} value={text(receiver.organisationName)} />
          <Cell label={t('referrals.xref01_department')} value={text(receiver.departmentName)} />
          <Cell label={t('referrals.xref01_unit')} value={text(receiver.unitName)} />
          <Cell label={t('referrals.xref01_address')} value={address(receiver)} />
        </div>
      </section>

      <section className={SECTION}>
        <Heading>{t('referrals.xref01_section_referrer')}</Heading>
        <div className={GRID}>
          <Cell label={t('referrals.xref01_referrer_name')} value={text(referrer.personName)} />
          <Cell label={t('referrals.xref01_referrer_title')} value={text(referrer.personTitle)} />
          <Cell label={t('referrals.xref01_identifier')} value={referrerId || null} />
        </div>
      </section>

      <section className={SECTION}>
        <Heading>{t('referrals.xref01_section_diagnoses')}</Heading>
        <div className={GRID}>
          <Cell
            label={t('referrals.xref01_main_diagnosis')}
            value={diagnosis(referral.referral_diagnosis_code, referral.referral_diagnosis)}
          />
          <Cell label={t('referrals.xref01_diagnosis_kind')} value={referDescriptionDisplay(text(d.referDescriptionCode))} />
          <Cell label={t('referrals.xref01_diagnosis_code_type')} value={text(d.referDiagnoseTypeCode)} />
        </div>
        {additional.map((item, index) => (
          <div key={index} className={`${GRID} border-t border-ds-line `}>
            <Cell
              label={t('referrals.xref01_additional_diagnosis')}
              value={diagnosis(text(item.diagnoseCode), text(item.diagnoseText))}
            />
            <Cell label={t('referrals.xref01_diagnosis_kind')} value={text(item.diagnoseDescriptionCode)} />
            <Cell label={t('referrals.xref01_diagnosis_code_type')} value={text(item.diagnoseTypeCode)} />
          </div>
        ))}
      </section>

      <section className={SECTION}>
        <Block label={t('referrals.xref01_requested')} value={text(d.clinicalReason)} />
        <Block label={t('referrals.xref01_supplementary')} value={text(d.supplementary)} last />
      </section>

      {visits.length > 0 && (
        <section className={SECTION}>
          <Heading>{t('referrals.xref01_section_hospital_visits')}</Heading>
          {visits.map((visit, index) => (
            <Block
              key={index}
              label={text(visit.informationCode) ?? t('referrals.xref01_hospital_visit')}
              value={
                Array.isArray(visit.information)
                  ? visit.information.filter((line) => typeof line === 'string').join('\n') || null
                  : null
              }
              last={index === visits.length - 1}
            />
          ))}
        </section>
      )}
    </div>
  );
}
